import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Avatar, Divider, Alert, CircularProgress, Paper, Grid, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Description as DocumentIcon, Assignment as TaskIcon, History as HistoryIcon, Person as PersonIcon, Schedule as ScheduleIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Forward as ForwardIcon, Edit as EditIcon, Visibility as ViewIcon, Download as DownloadIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../api/superAdminAPI';
import { useDateFormat, DATE_FORMATS } from '../../context/DateFormatContext';
import { formatShamsiDate, formatGregorianDate, formatHijriDate } from '../../utils/dateUtils';

const DocumentFlowViewer = () => {
  const { t, i18n } = useTranslation();
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { dateFormat } = useDateFormat();
  const isRtl = ['dari', 'ps'].includes(i18n.language);
  const [documentFlow, setDocumentFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [taskAssignments, setTaskAssignments] = useState([]);

  useEffect(() => {
    fetchDocumentFlow();
  }, [documentId]);

  const fetchDocumentFlow = async () => {
    try {
      setLoading(true);
      setError(null);
      const [documentFlowData, taskAssignmentsData] = await Promise.all([
        superAdminAPI.getDocumentFlow(documentId),
        superAdminAPI.getAllTaskAssignmentsData()
      ]);
      setDocumentFlow(documentFlowData.data);
      setTaskAssignments(taskAssignmentsData.data?.taskAssignments || []);
    } catch (err) {
      console.error(t('superadmin:error_fetching_document_flow'), err);
      setError(err.message || t('superadmin:failed_to_load_document_flow'));
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (event) => {
    switch (event.action) {
      case 'CREATED':
        return <DocumentIcon />;
      case 'ASSIGNED':
        return <TaskIcon />;
      case 'RECEIVED':
        return <CheckCircleIcon />;
      case 'FORWARDED':
        return <ForwardIcon />;
      case 'STATUS_CHANGED':
        return <EditIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  const getEventColor = (event) => {
    switch (event.action) {
      case 'CREATED':
        return 'primary';
      case 'ASSIGNED':
        return 'info';
      case 'RECEIVED':
        return 'success';
      case 'FORWARDED':
        return 'warning';
      case 'STATUS_CHANGED':
        return 'secondary';
      case 'COMPLETED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper to get latest task assignment status for a document
  const getDocumentTaskStatus = (docId) => {
    if (!taskAssignments || !Array.isArray(taskAssignments)) {
      return null;
    }
    const assignment = taskAssignments.find(t => t.document_id === docId);
    return assignment?.status || null;
  };

  // Helper to get status color for task assignment statuses
  const getTaskStatusColor = (status) => ({
    ASSIGNED: 'primary',
    ACCEPTED: 'success',
    RECEIVED: 'success',
    IN_PROGRESS: 'info',
    COMPLETED: 'success',
    REJECTED: 'error',
    REJECTED_RECEIPT: 'error',
    FORWARDED: 'warning',
    SKIPPED: 'secondary'
  }[status] || 'default');

  // Helper to get status label for task assignment statuses
  const getTaskStatusLabel = (status) => {
    const key = `superadmin:${status.toLowerCase()}`;
    const translated = t(key, status);
    return translated;
  };

  // Format date based on selected date format
  const formatDate = (dateString, showTime = false) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    return dateFormat === DATE_FORMATS.SHAMSI ? formatShamsiDate(date, isRtl, showTime) :
      dateFormat === DATE_FORMATS.QAMARI ? formatHijriDate(date, isRtl, showTime) :
      formatGregorianDate(date, isRtl, showTime);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'ASSIGNED': { color: 'primary', label: t('superadmin:assigned') },
      'ACCEPTED': { color: 'success', label: t('superadmin:accepted') },
      'IN_PROGRESS': { color: 'info', label: t('superadmin:in_progress') },
      'COMPLETED': { color: 'success', label: t('superadmin:completed') },
      'REJECTED': { color: 'error', label: t('superadmin:rejected') },
      'REJECTED_RECEIPT': { color: 'error', label: t('superadmin:rejected_receipt') },
      'FORWARDED': { color: 'warning', label: t('superadmin:forwarded') },
      'DRAFT': { color: 'default', label: t('superadmin:draft') },
      'CREATED': { color: 'primary', label: t('superadmin:created') },
      'RECEIVED': { color: 'success', label: t('superadmin:received') },
      'STATUS_CHANGED': { color: 'info', label: t('superadmin:status_changed') }
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
  };

  const getLocalizedDescription = (event) => {
    const { action, description, user, metadata } = event;
    
    switch (action) {
      case 'CREATED':
        return t('superadmin:document_created');
      case 'ASSIGNED':
        if (metadata?.assigned_to_user) {
          return t('superadmin:document_assigned_to_user', { user: metadata.assigned_to_user });
        }
        return t('superadmin:document_assigned');
      case 'RECEIVED':
        if (metadata?.received_by_user) {
          return t('superadmin:document_physically_received_by', { user: metadata.received_by_user });
        }
        return t('superadmin:document_received');
      case 'FORWARDED':
        if (metadata?.forwarded_to_user) {
          return t('superadmin:forwarded_task_to', { 
            user: user?.full_name || user?.username, 
            target: metadata.forwarded_to_user 
          });
        }
        return t('superadmin:document_forwarded');
      case 'STATUS_CHANGED':
        if (metadata?.new_status === 'COMPLETED') {
          return t('superadmin:task_completed_by', { user: user?.full_name || user?.username });
        }
        return description || t('superadmin:status_changed');
      default:
        return description || action;
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventDetailsOpen(true);
  };

  const handleCloseEventDetails = () => {
    setSelectedEvent(null);
    setEventDetailsOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('superadmin:error_loading_document_flow')}: {error}
        </Alert>
      </Box>
    );
  }

  if (!documentFlow) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {t('superadmin:no_document_flow_found')}
        </Alert>
      </Box>
    );
  }

  const { document, flow } = documentFlow;

  return (
    <Box sx={{ p: 2, direction: 'rtl', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: 'row-reverse' }}>
        <IconButton onClick={() => navigate(-1)} sx={{ ml: 1, bgcolor: 'white', boxShadow: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, textAlign: 'right' }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5, color: '#1976d2' }}>
            {t('superadmin:document_flow')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {document.title} - {document.reference_number}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDocumentFlow}
          sx={{ ml: 1, bgcolor: 'white', borderColor: '#1976d2', color: '#1976d2' }}
        >
          {t('common:refresh')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => console.log(t('superadmin:export_document_flow'))}
          sx={{ bgcolor: 'white', borderColor: '#1976d2', color: '#1976d2' }}
        >
          {t('common:download')}
        </Button>
      </Box>

      {/* Document Summary */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', flexDirection: 'row-reverse', color: '#1976d2', fontSize: '1.1rem' }}>
                <DocumentIcon sx={{ ml: 1, fontSize: '1.2rem' }} />
                {t('superadmin:document_details')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>{document.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('superadmin:title_label')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>{document.reference_number}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('superadmin:reference_label')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Box sx={{ textAlign: 'right' }}>
                    {(() => {
                      const taskStatus = getDocumentTaskStatus(document.id);
                      if (!taskStatus) {
                        // Fallback to document status if no task assignment
                        return getStatusChip(document.status);
                      }
                      
                      // Only use valid task assignment statuses
                      const validTaskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED', 'ACCEPTED', 'RECEIVED', 'REJECTED_RECEIPT'];
                      if (!validTaskStatuses.includes(taskStatus)) {
                        return getStatusChip(document.status);
                      }
                      
                      return (
                        <Chip
                          label={getTaskStatusLabel(taskStatus)}
                          color={getTaskStatusColor(taskStatus)}
                          size="small"
                          variant="outlined"
                        />
                      );
                    })()}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('superadmin:status_label')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>{document.type}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('superadmin:type_label')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', flexDirection: 'row-reverse', color: '#1976d2', fontSize: '1.1rem' }}>
                <PersonIcon sx={{ ml: 1, fontSize: '1.2rem' }} />
                {t('superadmin:people_involved')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>
                    {document.creator?.full_name || document.creator?.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('superadmin:creator_label')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>
                    {document.currentAssignee?.full_name || document.currentAssignee?.username || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('superadmin:current_assignee_label')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>
                    {document.created_at ? formatDate(document.created_at, true) : '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('superadmin:created_at_label')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Flow Timeline */}
      <Card sx={{ boxShadow: 2, borderRadius: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', flexDirection: 'row-reverse', color: '#1976d2', fontWeight: 'bold', fontSize: '1.1rem' }}>
            <ScheduleIcon sx={{ ml: 1, fontSize: '1.2rem' }} />
            {t('superadmin:complete_flow_timeline')}
          </Typography>
          
          {flow && flow.length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              {/* Timeline line */}
              <Box
                sx={{
                  position: 'absolute',
                  right: 20,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  bgcolor: '#1976d2',
                  zIndex: 1
                }}
              />
              
              <List sx={{ p: 0 }}>
                {flow.map((event, index) => (
                  <ListItem
                    key={`${event.type}-${event.id}`}
                    sx={{
                      position: 'relative',
                      pr: 5,
                      pb: 1.5,
                      '&:last-child': { pb: 0 }
                    }}
                  >
                    {/* Timeline dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 14,
                        top: 16,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: `${getEventColor(event)}.main`,
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.6rem'
                      }}
                    >
                      {React.cloneElement(getEventIcon(event), { sx: { fontSize: '0.6rem' } })}
                    </Box>

                    <Paper
                      sx={{
                        p: 1.5,
                        width: '100%',
                        cursor: 'pointer',
                        '&:hover': { 
                          bgcolor: '#f8f9fa',
                          boxShadow: 2
                        },
                        boxShadow: 1,
                        borderRadius: 1,
                        border: '1px solid #e9ecef',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => handleEventClick(event)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexDirection: 'row-reverse' }}>
                        <Avatar
                          src={event.user?.avatar}
                          sx={{ width: 28, height: 28, ml: 1.5, bgcolor: '#1976d2', fontSize: '0.75rem' }}
                        >
                          {event.user?.full_name?.charAt(0) || event.user?.username?.charAt(0) || 'U'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.25, fontSize: '0.85rem' }}>
                            {event.user?.full_name || event.user?.username || t('superadmin:unknown_user')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {t(`superadmin:${event.action.toLowerCase()}_action`) || t(`superadmin:${event.action.toLowerCase()}`) || event.action}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: 'row-reverse' }}>
                          <Chip
                            label={t(`superadmin:${event.type.toLowerCase()}`) || event.type}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', bgcolor: '#e9ecef', px: 0.75, py: 0.25, borderRadius: 0.5 }}>
                            {event.timestamp ? formatDate(event.timestamp, true) : '-'}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 0.5, textAlign: 'right', fontSize: '0.8rem', lineHeight: 1.4 }}>
                        {getLocalizedDescription(event)}
                      </Typography>
                      {event.metadata?.status && event.metadata.status !== 'null' && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexDirection: 'row-reverse' }}>
                          {getStatusChip(event.metadata.status)}
                        </Box>
                      )}
                    </Paper>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Alert severity="info">
              {t('superadmin:no_flow_events_found')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Event Details Dialog */}
      <Dialog
        open={eventDetailsOpen}
        onClose={handleCloseEventDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('superadmin:event_details')}
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:type')}
                  </Typography>
                  <Typography variant="body1">
                    {t(`superadmin:${selectedEvent.type.toLowerCase()}`) || selectedEvent.type}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:action')}
                  </Typography>
                  <Typography variant="body1">
                    {t(`superadmin:${selectedEvent.action.toLowerCase()}_action`) || t(`superadmin:${selectedEvent.action.toLowerCase()}`) || selectedEvent.action}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:user')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.user?.full_name || selectedEvent.user?.username || t('superadmin:unknown_user')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:timestamp')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.timestamp ? formatDate(selectedEvent.timestamp, true) : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:description')}
                  </Typography>
                  <Typography variant="body1">
                    {getLocalizedDescription(selectedEvent)}
                  </Typography>
                </Grid>
                {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('superadmin:metadata')}
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedEvent.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEventDetails}>
            {t('common:close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentFlowViewer;